import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import type { OpenClawLocalDiagnostics } from "../provider-hosts/openclaw/diagnostics";
import { formatWakeLoopCliCommand } from "../runtime/cli-entry-command";
import { resolveGatewayChainRepairGuidance } from "./gateway-chain-readiness";
import { resolveOpenClawDiagnosticsGuidance } from "./provider-diagnostics/openclaw/guidance";
import type {
  AgentReplyReadinessStatus,
  ReplyReadinessReasonCode,
} from "./reply-readiness";
import { resolveServiceParticipationReadiness } from "./service-participation-readiness";

export type ReplyReadinessGuidance = {
  primaryReasonCode: ReplyReadinessReasonCode | null;
  summary: string;
  nextSteps: string[];
};

export function buildReplyReadinessGuidance(input: {
  openClawLocalDiagnostics?: OpenClawLocalDiagnostics | null;
  profile: WakeLoopProfile;
  replyReadiness: AgentReplyReadinessStatus;
}): ReplyReadinessGuidance {
  if (input.replyReadiness.replyReadiness === "ready") {
    return {
      primaryReasonCode: null,
      summary:
        'This agent can reply in a space when you mention it with "@Name(wake)".',
      nextSteps: [
        formatWakeLoopCliCommand("wakeloop space join YOUR_SPACE_ID"),
        'Send a message with an exact mention like "@Name(wake)".',
      ],
    };
  }

  const primaryReasonCode =
    input.replyReadiness.reasonCodes[0] ?? "route.not_registered";
  const serviceGuidance = buildServiceReplyReadinessGuidance({
    primaryReasonCode,
    replyReadiness: input.replyReadiness,
  });

  if (serviceGuidance) {
    return serviceGuidance;
  }

  const openClawGuidance = buildOpenClawReplyReadinessGuidance({
    diagnostics: input.openClawLocalDiagnostics ?? null,
    primaryReasonCode,
    profile: input.profile,
  });
  if (openClawGuidance) {
    return openClawGuidance;
  }

  return {
    primaryReasonCode,
    summary: buildReplyReadinessGuidanceSummary(primaryReasonCode),
    nextSteps: buildReplyReadinessNextSteps({
      localAgentId: input.replyReadiness.localAgentId,
      profile: input.profile,
      reasonCode: primaryReasonCode,
    }),
  };
}

function buildOpenClawReplyReadinessGuidance(input: {
  diagnostics: OpenClawLocalDiagnostics | null;
  primaryReasonCode: ReplyReadinessReasonCode;
  profile: WakeLoopProfile;
}): ReplyReadinessGuidance | null {
  if (
    !input.diagnostics ||
    (input.primaryReasonCode !== "controller.bootstrap.failed" &&
      input.primaryReasonCode !== "controller.gateway.unhealthy")
  ) {
    return null;
  }
  const guidance = resolveOpenClawDiagnosticsGuidance({
    diagnostics: input.diagnostics,
    profileId: input.profile.wakeLoopProfileId,
  });
  return {
    primaryReasonCode: input.primaryReasonCode,
    summary: guidance.summary,
    nextSteps: guidance.nextCommands.map((command) =>
      formatWakeLoopCliCommand(command)
    ),
  };
}

function buildServiceReplyReadinessGuidance(input: {
  primaryReasonCode: ReplyReadinessReasonCode;
  replyReadiness: AgentReplyReadinessStatus;
}): ReplyReadinessGuidance | null {
  switch (input.primaryReasonCode) {
    case "service.not_installed":
    case "service.drifted":
    case "service.not_running":
    case "dispatch.storage_not_ready":
    case "service.gateway_chain_unhealthy": {
      const serviceParticipationReadiness =
        resolveServiceParticipationReadiness({
          deviceReplyReadiness: input.replyReadiness.deviceReplyReadiness,
        });
      return {
        primaryReasonCode: input.primaryReasonCode,
        summary: serviceParticipationReadiness.detailText,
        nextSteps: serviceParticipationReadiness.nextSteps,
      };
    }
    default:
      return null;
  }
}

function buildReplyReadinessGuidanceSummary(
  reasonCode: ReplyReadinessReasonCode
): string {
  switch (reasonCode) {
    case "local_agents.none_needed":
      return "This device does not need WakeLoop Service until you choose local agents to bring into a space.";
    case "profile.inactive":
      return "Choose an active agent profile first.";
    case "profile.unbound":
      return "This profile is not connected to an agent yet.";
    case "controller.binding.disabled":
      return "This profile's agent is turned off.";
    case "projection.missing":
      return "WakeLoop is still setting up this device.";
    case "controller.registry.disabled":
      return "This agent is turned off in WakeLoop.";
    case "controller.launch.choice_required":
      return "WakeLoop found more than one install for this agent on this device.";
    case "controller.launch.needs_repair":
      return "WakeLoop can't start this agent on this device yet.";
    case "workspace.missing":
      return "This profile is missing its local workspace on this device.";
    case "workspace.invalid":
      return "This profile's local workspace is broken on this device.";
    case "runtime.adapter.unsupported":
      return "This agent setup can't reply from this device.";
    case "controller.bootstrap.failed":
      return "WakeLoop couldn't finish setting up OpenClaw for this profile.";
    case "controller.gateway.unhealthy":
      return "OpenClaw is not connected on this device yet.";
    case "route.offline":
      return "WakeLoop can start this agent here, but the server can't reach this device right now.";
    case "service.gateway_chain_unhealthy":
      return "WakeLoop can't reach the gateway for this device yet.";
    case "dispatch.storage_not_ready":
      return "WakeLoop shared storage is temporarily unavailable.";
    case "route.not_registered":
      return "WakeLoop is still setting up replies for this profile on this device.";
    default:
      return "This profile can't reply until local setup is fixed.";
  }
}

function buildReplyReadinessNextSteps(input: {
  localAgentId?: string | null;
  profile: WakeLoopProfile;
  reasonCode: ReplyReadinessReasonCode;
}): string[] {
  const profileId = input.profile.wakeLoopProfileId;

  switch (input.reasonCode) {
    case "local_agents.none_needed":
      return [
        formatWakeLoopCliCommand("wakeloop start"),
        formatWakeLoopCliCommand("wakeloop service install"),
        formatWakeLoopCliCommand("wakeloop service start"),
      ];
    case "profile.inactive":
      return [
        formatWakeLoopCliCommand("wakeloop profile list"),
        formatWakeLoopCliCommand("wakeloop profile set <agent-profile-id>"),
      ];
    case "profile.unbound":
      return ["Open this Agent in WakeLoop Web and choose a local agent."];
    case "controller.binding.disabled":
      return [
        "Open this Agent in WakeLoop Web and turn on its local agent connection.",
      ];
    case "projection.missing":
      return [
        formatWakeLoopCliCommand(
          `wakeloop service status --profile ${profileId}`
        ),
      ];
    case "controller.registry.disabled":
      return [
        formatWakeLoopCliCommand("wakeloop agents enable --agent <agent-id>"),
        formatWakeLoopCliCommand("wakeloop agents list"),
      ];
    case "controller.launch.choice_required":
      return buildLocalAgentInstallChoiceNextSteps({
        localAgentId: input.localAgentId,
      });
    case "controller.launch.needs_repair":
      return buildLocalAgentLaunchRepairNextSteps({
        localAgentId: input.localAgentId,
      });
    case "workspace.missing":
    case "workspace.invalid":
      return buildLocalAgentLaunchRepairNextSteps({
        localAgentId: input.localAgentId,
      });
    case "runtime.adapter.unsupported":
      return [
        formatWakeLoopCliCommand("wakeloop agents detect"),
        formatWakeLoopCliCommand("wakeloop agents list"),
      ];
    case "controller.bootstrap.failed":
      return [
        ...buildLocalAgentLaunchRepairNextSteps({
          localAgentId: input.localAgentId,
        }),
        formatWakeLoopCliCommand("openclaw agents list"),
      ];
    case "controller.gateway.unhealthy":
      return [
        ...buildLocalAgentLaunchRepairNextSteps({
          localAgentId: input.localAgentId,
        }),
        formatWakeLoopCliCommand("openclaw health --json"),
      ];
    case "route.offline":
      return [
        formatWakeLoopCliCommand(
          `wakeloop service status --profile ${profileId}`
        ),
        formatWakeLoopCliCommand("wakeloop service repair"),
      ];
    case "route.not_registered":
      return [
        formatWakeLoopCliCommand(
          `wakeloop service status --profile ${profileId}`
        ),
        formatWakeLoopCliCommand("wakeloop doctor --repair"),
      ];
    case "service.gateway_chain_unhealthy":
      return resolveGatewayChainRepairGuidance({}).nextSteps;
    case "dispatch.storage_not_ready":
      return resolveGatewayChainRepairGuidance({
        failureKind: "dispatch_storage_not_ready",
      }).nextSteps;
    default:
      return [formatWakeLoopCliCommand("wakeloop doctor --repair")];
  }
}

function buildLocalAgentInstallChoiceNextSteps(input: {
  localAgentId?: string | null;
}): string[] {
  const agentId = normalizeOptionalText(input.localAgentId);
  if (!agentId) {
    return [
      formatWakeLoopCliCommand("wakeloop agents enable --agent <agent-id>"),
      formatWakeLoopCliCommand("wakeloop agents list"),
    ];
  }

  return [
    formatWakeLoopCliCommand(`wakeloop agents enable --agent ${agentId}`),
    formatWakeLoopCliCommand(`wakeloop agents repair --agent ${agentId}`),
  ];
}

function buildLocalAgentLaunchRepairNextSteps(input: {
  localAgentId?: string | null;
}): string[] {
  const agentId = normalizeOptionalText(input.localAgentId);
  if (!agentId) {
    return [
      formatWakeLoopCliCommand("wakeloop agents repair --agent <agent-id>"),
      formatWakeLoopCliCommand("wakeloop agents list"),
    ];
  }

  return [
    formatWakeLoopCliCommand(`wakeloop agents repair --agent ${agentId}`),
  ];
}

function normalizeOptionalText(value: unknown): string | null {
  const normalized = String(value ?? "").trim();
  return normalized.length > 0 ? normalized : null;
}
